import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProgressService } from '../../service/progress.service';
import { BaseModalComponent } from '../../common/modal/base-modal/base-modal.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Contest } from '../../model/contest/Contest';
import { Role } from '../../model/auth/Role';
import { NotificationHandler } from '../../helper/notification.handler';
import { ContestService } from '../../service/contest.service';
import { CompetitionService } from '../../service/competition.service';
import { TenantService } from '../../service/tenant.service';
import { EnrolData, User } from '../../model/auth/User';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { ErrorInterceptor } from '../../helper/error.interceptor';

export interface ContestPaymentData {
  paymentPageUrl?: string;
  exemptEnrol?: boolean;
  freeEnrol?: boolean;

  group?: EnrolData[];

  userId?: string;
  contest?: Contest;
  role?: Role;
}

@Component({
  selector: 'app-contest-payment-modal',
  templateUrl: './contest-payment-modal.component.html',
  styleUrls: ['./contest-payment-modal.component.scss']
})
export class ContestPaymentModalComponent extends BaseModalComponent implements OnInit, OnDestroy {

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  readonly paymentPageUrl: SafeResourceUrl;
  readonly exemptEnrol: boolean;
  readonly freeEnrol: boolean;

  readonly userId?: string;
  readonly contest?: Contest;
  readonly role?: Role;

  readonly group?: EnrolData[];

  hosts?: User[];

  private breakpointChangeSubscription: Subscription | undefined;
  xSmallScreenView = false;

  constructor(private contestService: ContestService,
              private competitionService: CompetitionService,
              private tenantService: TenantService,
              private breakpointObserver: BreakpointObserver,
              sanitizer: DomSanitizer,
              dialogRef: MatDialogRef<ContestPaymentModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: ContestPaymentData) {
    super(dialogRef, progressService);

    this.paymentPageUrl = data.paymentPageUrl ? sanitizer.bypassSecurityTrustResourceUrl(data.paymentPageUrl) : '';
    this.exemptEnrol = data.exemptEnrol == true;
    this.freeEnrol = data.freeEnrol == true;

    this.userId = data.userId;
    this.contest = data.contest;
    this.role = data.role;

    this.group = data.group;
  }

  ngOnInit(): void {
    if (this.exemptEnrol) this.loadHostInfo();

    this.breakpointChangeSubscription = this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .subscribe((state: BreakpointState) => {
        this.xSmallScreenView = state.breakpoints[Breakpoints.XSmall];
      });
  }

  ngOnDestroy(): void {
    if (this.breakpointChangeSubscription) {
      this.breakpointChangeSubscription.unsubscribe();
    }
  }

  proceedAllowed(): boolean {
    return true;
  }

  loadHostInfo() {
    this.startProgress();

    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionService.getContactHosts(tenant.id).subscribe({
        next: hosts => {
          if (hosts && hosts.length > 0) {
            this.hosts = hosts || [];
          }
        }, error: error => {
          NotificationHandler.handleError($localize`:Notifications|Competition hosts fetch has failed message@@notifications-competition-hosts-fetch-failed-error:Competition hosts fetch has failed`, error);
        }, complete: () => {
          this.stopProgress();
        }
      });
    }
  }

  proceed(): void {
    if (this.group && this.group.length > 0) {
      this.enrolGroupFree();
      return;
    }

    if (this.freeEnrol && this.contest) {
      this.startProgress();

      this.contestService.enrol(this.contest.id, this.userId, this.role, undefined, undefined, false, true).subscribe({
        next: user => {
          this.stopProgress();

          if (user.paymentNotConfigured) {
            NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, false);
          }

          this.closeModal({
            contest: this.contest,
          });
        }, error: error => {
          this.stopProgress();

          const code = ErrorInterceptor.getStatusCode(error);

          if (code == 412) {
            NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, error, false);
          } else {
            NotificationHandler.handleError($localize`:Notifications|Contest user enrollment has failed message@@notifications-contest-user-enrollment-failed-error:Contest user enrollment has failed`, error);
          }
        }
      });
    }
  }

  enrolGroupFree() {
    if (!this.contest || !this.group) return;

    this.startProgress();

    this.group.forEach(user => user.free_enrol = true);

    this.contestService.enrolGroup(this.contest.id, this.group).subscribe({
      next: group => {
        this.stopProgress();

        if (group.paymentNotConfigured) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, false);
        }

        this.closeModal({
          contest: this.contest,
        });
      }, error: error => {
        this.stopProgress();

        const code = ErrorInterceptor.getStatusCode(error);

        if (code == 412) {
          NotificationHandler.handleWarning($localize`:Notifications|Tenant payment provider not configured message@@notifications-tenant-payment-provider-not-configured-error:The Host does not configure a payment provider for this competition. Please get in touch with the Host directly to proceed with payment.`, error, false);
        } else {
          NotificationHandler.handleError($localize`:Notifications|Contest enrol has failed message@@notifications-contest-enrol-failed-error:Contest enrol has failed`, error);
        }
      }
    });
  }

}
